import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Container,
  Box,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authenticatedFetch } from '../../utils/api';
import { useTranslation } from '../../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Employee {
  id: number;
  name: string;
  phone: string;
  address: string;
  created_at: string;
  updated_at: string;
}

const ListEmployees = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();
  const { translations: t } = useTranslation();

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/employees`);
      if (!response.ok) {
        throw new Error('Failed to fetch employees');
      }

      const data = await response.json();
      setEmployees(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    navigate('/employees/new');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 } }}>
      <Box 
        display="flex" 
        justifyContent="flex-end" 
        alignItems="center" 
        mb={{ xs: 2, sm: 3 }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateNew}
          sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: '160px' } }}
        >
          {t.employees.createNew}
        </Button>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <TableContainer component={Paper}>
        <Table aria-label="employees table">
          <TableHead>
            <TableRow>
              <TableCell>{t.employees.name}</TableCell>
              <TableCell>{t.employees.phone}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t.companies.address}</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{t.date}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {t.employees.noEmployees}
                </TableCell>
              </TableRow>
            ) : (
              employees.map((employee) => (
                <TableRow 
                    key={employee.id}
                    onClick={() => navigate(`/employees/${employee.id}`)}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {employee.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: { sm: 'none' } }}>
                      {employee.phone}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{employee.phone}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{employee.address}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    {new Date(employee.created_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default ListEmployees;